// Platform Request #14: the "Issue Suspension Letter" action, placed inside
// DriverStateControl.tsx's suspended-state panel. Two explicit steps, never
// collapsed into one: Generate preview (writes a draft, sends nothing) and
// Confirm & Send (the only thing in this component that can reach a
// driver's inbox). Closing or cancelling at either step sends nothing.
//
// alleged_misconduct and suspension_ground start blank every time this
// dialog opens, on purpose -- see the reset in the effect below -- and are
// never pre-filled from anything, including the driver's own state-change
// reason typed elsewhere in DriverStateControl.tsx.
import { useEffect, useState } from 'react'
import { FileWarning, Send } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../lib/shadcn/dialog'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetSuspensionLetterDelivery,
  usePreviewSuspensionLetter,
  useResendSuspensionLetter,
  useSendSuspensionLetter,
} from '../../hooks/backend/interviews'

// One row of v_suspension_letter_delivery (db/migrations/2841). The sentence is
// written in Postgres; this screen shows it and colours it, nothing more.
type Delivery = {
  letter_id: string
  letter_status: string
  effective_suspension_date: string
  created_at: string
  issued_by_email: string | null
  delivery_state: 'delivered' | 'sending' | 'delayed' | 'failed' | 'not_queued' | 'not_sent' | 'draft'
  delivery_note: string
  messages_failed: number
}

const DELIVERY_TONE: Record<Delivery['delivery_state'], string> = {
  delivered: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  sending: 'border-border bg-muted/40 text-foreground',
  draft: 'border-border bg-muted/40 text-foreground',
  delayed: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  failed: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200',
  not_queued: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200',
  not_sent: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200',
}

const DELIVERY_LABEL: Record<Delivery['delivery_state'], string> = {
  delivered: 'Delivered',
  sending: 'Sending',
  draft: 'Not sent',
  delayed: 'Delayed',
  failed: 'Failed',
  not_queued: 'Not sent',
  not_sent: 'Not sent',
}

type PreviewResult = {
  letter_id: string
  driver_name: string
  base64: string
  filename: string
  content_type: string
}

const EMPTY_FORM = {
  incident_date: '',
  alleged_misconduct: '',
  suspension_ground: '',
  fleetly_contact_title: '',
  fleetly_representative_title: '',
}

export function SuspensionLetterAction({
  applicationId,
  onSaved,
}: {
  applicationId: string
  onSaved: () => void
}) {
  const { trigger: preview } = usePreviewSuspensionLetter<PreviewResult>()
  const { trigger: send } = useSendSuspensionLetter<{ ok: boolean; letter_id: string }>()
  const delivery = useGetSuspensionLetterDelivery<Delivery[]>()
  const loadDelivery = delivery.trigger
  const { trigger: resend } = useResendSuspensionLetter<{ requeued: number }>()
  const [resending, setResending] = useState<string | null>(null)

  // Platform Requests #14 and #23: a letter that looked sent and never arrived.
  // What happened to each email is shown under the button, every time the
  // driver is opened, rather than trusted from a toast.
  useEffect(() => {
    loadDelivery({ application_id: applicationId }, { skipCache: true })
  }, [applicationId, loadDelivery])

  const handleResend = async (letterId: string) => {
    setResending(letterId)
    try {
      const r = await resend({ application_id: applicationId, letter_id: letterId }).result
      toast.success(
        `${r?.requeued ?? 0} failed email${r?.requeued === 1 ? '' : 's'} queued again. The result shows here within a few minutes.`,
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not send the letter again', { description: msg })
    } finally {
      setResending(null)
      loadDelivery({ application_id: applicationId }, { skipCache: true })
    }
  }

  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<'form' | 'preview'>('form')
  const [form, setForm] = useState(EMPTY_FORM)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<PreviewResult | null>(null)

  // Every open starts from a fully blank form. Never seeded from a previous
  // letter, this driver's deactivation reason, or anything else.
  useEffect(() => {
    if (open) {
      setStep('form')
      setForm(EMPTY_FORM)
      setResult(null)
    }
  }, [open, applicationId])

  const canGeneratePreview =
    form.incident_date.trim() !== '' &&
    form.alleged_misconduct.trim() !== '' &&
    form.suspension_ground.trim() !== '' &&
    form.fleetly_contact_title.trim() !== '' &&
    form.fleetly_representative_title.trim() !== ''

  const handleGeneratePreview = async () => {
    setBusy(true)
    try {
      const r = await preview({
        application_id: applicationId,
        incident_date: form.incident_date,
        alleged_misconduct: form.alleged_misconduct.trim(),
        suspension_ground: form.suspension_ground.trim(),
        fleetly_contact_title: form.fleetly_contact_title.trim(),
        fleetly_representative_title: form.fleetly_representative_title.trim(),
      }).result
      setResult(r)
      setStep('preview')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not generate the letter', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  const handleConfirmSend = async () => {
    if (!result) return
    setBusy(true)
    try {
      await send({ application_id: applicationId, letter_id: result.letter_id }).result
      // Queued, not delivered: the worker sends within a couple of minutes, and
      // the delivery line under the button says whether it actually went.
      toast.success(`Suspension letter queued for ${result.driver_name}`, {
        description:
          'It normally leaves within two minutes, with your copy. Whether it arrived is shown under the Issue suspension letter button.',
      })
      setOpen(false)
      loadDelivery({ application_id: applicationId }, { skipCache: true })
      onSaved()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      toast.error('Could not send the letter', { description: msg })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-11 justify-center sm:h-9"
      >
        <FileWarning className="h-4 w-4" />
        Issue suspension letter
      </Button>

      {delivery.error ? (
        <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
          Could not check whether earlier letters arrived: {delivery.error}. Reload the driver to try again.
        </p>
      ) : (
        Array.isArray(delivery.data) &&
        delivery.data.length > 0 && (
          <ul className="mt-2 space-y-2">
            {delivery.data.map((d) => (
              <li
                key={d.letter_id}
                className={`rounded-md border p-2.5 text-xs ${DELIVERY_TONE[d.delivery_state] ?? DELIVERY_TONE.sending}`}
              >
                <p className="font-medium">
                  Suspension letter of {d.created_at.slice(0, 10)}: {DELIVERY_LABEL[d.delivery_state] ?? d.delivery_state}
                </p>
                <p className="mt-0.5">{d.delivery_note}</p>
                {d.delivery_state === 'failed' && d.messages_failed > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => handleResend(d.letter_id)}
                    disabled={resending !== null}
                  >
                    {resending === d.letter_id ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                    Send again
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-2xl">
          {step === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Issue suspension letter</DialogTitle>
                <DialogDescription>
                  Every field below is required and starts blank. None of them is filled in from the
                  reason typed when this driver was suspended. This letter does not include a hearing
                  date, time or venue, or an employment start date; those are handled separately.
                  Line breaks in the allegation are kept as paragraphs in the letter.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="incident_date">Incident date</Label>
                  <Input
                    id="incident_date"
                    type="date"
                    value={form.incident_date}
                    onChange={(e) => setForm((f) => ({ ...f, incident_date: e.target.value }))}
                    disabled={busy}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="alleged_misconduct">
                    Alleged misconduct <span className="text-muted-foreground">(allegation, not a conclusion)</span>
                  </Label>
                  <Textarea
                    id="alleged_misconduct"
                    value={form.alleged_misconduct}
                    onChange={(e) => setForm((f) => ({ ...f, alleged_misconduct: e.target.value }))}
                    placeholder="Describe what is alleged to have happened, as an allegation pending investigation."
                    disabled={busy}
                    rows={3}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="suspension_ground">
                    Suspension ground <span className="text-muted-foreground">(the contractual basis, kept separate from the allegation above)</span>
                  </Label>
                  <Textarea
                    id="suspension_ground"
                    value={form.suspension_ground}
                    onChange={(e) => setForm((f) => ({ ...f, suspension_ground: e.target.value }))}
                    placeholder="The clause or contractual ground for this suspension."
                    disabled={busy}
                    rows={2}
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="fleetly_contact_title">Your title (as the contact on this letter)</Label>
                    <Input
                      id="fleetly_contact_title"
                      value={form.fleetly_contact_title}
                      onChange={(e) => setForm((f) => ({ ...f, fleetly_contact_title: e.target.value }))}
                      disabled={busy}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fleetly_representative_title">
                      Your title (as the signing representative)
                    </Label>
                    <Input
                      id="fleetly_representative_title"
                      value={form.fleetly_representative_title}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fleetly_representative_title: e.target.value }))
                      }
                      disabled={busy}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button onClick={handleGeneratePreview} disabled={!canGeneratePreview || busy}>
                  {busy && <Spinner className="h-4 w-4" />}
                  Generate preview
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Review before sending: {result?.driver_name}</DialogTitle>
                <DialogDescription>
                  This is the exact PDF that will be attached and sent. Sending also copies the person
                  issuing it, Yolisa, Luyanda and admin@fleetly.co.za.
                </DialogDescription>
              </DialogHeader>

              {result && (
                <div className="overflow-hidden rounded-md border border-border" style={{ height: '60vh' }}>
                  <iframe
                    title="Suspension letter preview"
                    src={`data:${result.content_type};base64,${result.base64}`}
                    className="h-full w-full"
                  />
                </div>
              )}

              <DialogFooter>
                <Button variant="ghost" onClick={() => setStep('form')} disabled={busy}>
                  Back to edit
                </Button>
                <Button variant="destructive" onClick={handleConfirmSend} disabled={busy}>
                  {busy ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                  Confirm &amp; send
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
